import {
  BudgetTypeLabelModifier,
  type budgetType as budgetTypeEnum,
} from '@/features/master-table/models';
import type {
  ConsecutiveFiscalYearsModel,
  ConsecutiveRequestAmountsModel,
} from '@/features/project/_models/consecutive';
import type {
  ConsecutiveBudgetsModel,
  ConsecutiveExecutionsModel,
} from '@/features/project/projects/_models/consecutive';
import { t } from '@/i18n';
import {
  type AmountUnit,
  BigCalculator,
  FALLBACK_TEXT,
  formatNegativeValue,
  formatPercentage,
  formatValueByUnit,
  handleZeroValueWithNegativeCount,
} from '@/utils';
import { groupBy } from 'lodash-es';
import type { z } from 'zod';

export type BudgetAndExecutionTargetType =
  | z.infer<typeof budgetTypeEnum>
  | 'reserve'
  | 'execution'
  | 'requested'
  | 'total'
  | 'execution-rate'
  | 'others';

type ValueByYear = {
  year: number | undefined;
  value: number | string | null | undefined;
  hasNegativeValue: boolean;
};

export type BudgetAndExecutionTableType = {
  name: string;
  type: BudgetAndExecutionTargetType;
  hideInPdf?: boolean;
  valueByYear: ValueByYear[] | null;
  children?: Array<BudgetAndExecutionTableType>;
};

type ReturnBudgetAndExecutionTableResult = {
  tableDataSource: Array<BudgetAndExecutionTableType>;
  years: number[];
  tableYears: number[];
};

type GetBudgetAndExecutionTableDataArgs = {
  consecutiveFiscalYears:
    | z.infer<typeof ConsecutiveFiscalYearsModel>
    | undefined;
  consecutiveRequestAmounts:
    | z.infer<typeof ConsecutiveRequestAmountsModel>
    | undefined;
  consecutiveBudgets: z.infer<typeof ConsecutiveBudgetsModel> | undefined;
  consecutiveExecutions: z.infer<typeof ConsecutiveExecutionsModel> | undefined;
};

export const getBudgetAndExecutionTableData = ({
  consecutiveFiscalYears,
  consecutiveRequestAmounts,
  consecutiveBudgets,
  consecutiveExecutions,
}: GetBudgetAndExecutionTableDataArgs): ReturnBudgetAndExecutionTableResult => {
  const years =
    consecutiveFiscalYears?.map((d) => d.fiscalYear).sort((a, b) => a - b) ??
    [];
  const latestYear = years.at(-1);
  const tableYears = latestYear ? years.concat(latestYear + 1) : [];

  const {
    initial: initialData,
    supplementary: supplementaryData,
    'carried-forward': carriedForwardData,
    reserve: reserveData,
  } = groupBy(consecutiveBudgets, 'budgetType');

  const tableDataSource: Array<BudgetAndExecutionTableType> = [
    getRequestedAmountRow(tableYears, consecutiveRequestAmounts),
    getInitialBudgetRow({
      data: initialData,
      tableYears,
    }),
    getSupplementaryOrReserveBudgetRow({
      data: supplementaryData,
      type: 'supplementary',
      tableYears,
    }),
    getCarriedForwardBudgetRow(carriedForwardData, tableYears),
    getSupplementaryOrReserveBudgetRow({
      data: reserveData,
      tableYears,
      type: 'reserve',
    }),
    getTotalBudgetAmountRow(tableYears, consecutiveBudgets),
    getExecutionAmountRow(tableYears, consecutiveExecutions),
    getExecutionRate(tableYears, consecutiveBudgets, consecutiveExecutions),
    getOtherRows({
      tableYears,
      carriedForwardData,
      consecutiveBudgets,
      consecutiveExecutions,
    }),
  ];

  return {
    tableDataSource,
    years,
    tableYears,
  };
};

type ConvertValueArgs = {
  name: string;
  value: number | string | undefined | null;
  unit: AmountUnit;
  hasNegativeValue: boolean;
  isDebugMode?: boolean;
};

export const convertBudgetAndExecutionValue = ({
  value,
  name,
  unit,
  hasNegativeValue,
  isDebugMode,
}: ConvertValueArgs): string => {
  if (
    [
      t('execution-rate'),
      t('others'),
      t('budget-and-execution-ratio'),
    ].includes(name)
  ) {
    if (value && typeof value === 'string') {
      return formatNegativeValue(value);
    }
    return String(value ?? FALLBACK_TEXT);
  }

  if (typeof value === 'string') {
    return value;
  }

  const { isAggregateValueZero, fallbackValue } =
    handleZeroValueWithNegativeCount(value, {
      negativeCount: hasNegativeValue ? 1 : 0,
      isDebugMode,
    });

  if (isAggregateValueZero) {
    return fallbackValue;
  }

  return name === t('execution-amount')
    ? formatValueByUnit(value, { operation: 'floor', unit })
    : formatValueByUnit(value, { operation: 'round', unit });
};

export const convertBudgetTypeNameToOrderingKey = (name: string): number => {
  const orderMap: Record<string, number> = {
    [t('initial-budget')]: 0,
    [t('revenue-supplementary1')]: 1,
    [t('revenue-supplementary2')]: 2,
    [t('revenue-supplementary3')]: 3,
    [t('revenue-supplementary4')]: 4,
    [t('revenue-supplementary5')]: 5,
    [t('budget-brought-forward-from-prior')]: 6,
    [t('reserve-fund-1')]: 7,
    [t('reserve-fund-2')]: 8,
    [t('reserve-fund-3')]: 9,
    [t('reserve-fund-4')]: 10,
  };

  return orderMap[name] ?? 100;
};

const calculateTotalBudgetAmount = (
  fiscalYear: number,
  consecutiveBudgets?: z.infer<typeof ConsecutiveBudgetsModel>,
): number | undefined => {
  if (!consecutiveBudgets?.length) {
    return undefined;
  }
  const isAllNull = consecutiveBudgets.every(
    (d) => d.finalizedAmount === null || d.finalizedAmount === undefined,
  );
  // 箱だけある場合を考慮
  if (isAllNull) {
    return undefined;
  }
  return consecutiveBudgets.reduce((acc, cur) => {
    if (
      cur.fiscalYear !== fiscalYear ||
      typeof cur.finalizedAmount !== 'number'
    ) {
      return acc;
    }
    return BigCalculator.add(acc, cur.finalizedAmount);
  }, 0);
};

/// ////////////////////////////////////////////
/// 行の取得処理
/// ////////////////////////////////////////////

/** 要求額行の取得 */
const getRequestedAmountRow = (
  tableYears: number[],
  consecutiveRequestAmounts?: z.infer<typeof ConsecutiveRequestAmountsModel>,
): BudgetAndExecutionTableType => {
  return {
    type: 'requested',
    name: t('requested-amount'),
    valueByYear: tableYears.map((year) => {
      const requestAmount = consecutiveRequestAmounts?.find(
        (d) => d.fiscalYear === Number(year),
      );
      return {
        year,
        value: requestAmount?.amount,
        hasNegativeValue: (requestAmount?.negativeAmountCount ?? 0) > 0,
      };
    }),
  };
};

/** 当初予算行の取得 */
const getInitialBudgetRow = ({
  data,
  tableYears,
}: {
  data: z.infer<typeof ConsecutiveBudgetsModel> | undefined;
  tableYears: number[];
}): BudgetAndExecutionTableType => {
  return {
    type: 'initial',
    name: BudgetTypeLabelModifier.initial,
    valueByYear: tableYears.map((year) => {
      const item = data?.find((item) => item.fiscalYear === year);
      return {
        year,
        value: item?.finalizedAmount,
        hasNegativeValue: (item?.negativeFinalizedAmountCount ?? 0) > 0,
      };
    }),
  };
};

/** 補助予算行/予備費等の取得 */
const getSupplementaryOrReserveBudgetRow = ({
  data,
  type,
  tableYears,
}: {
  data: z.infer<typeof ConsecutiveBudgetsModel> | undefined;
  type: 'supplementary' | 'reserve';
  tableYears: number[];
}): BudgetAndExecutionTableType => {
  const groupedByBudgetTypeName = Object.entries(
    groupBy(data, 'budgetTypeName'),
  ).sort((a, b) => {
    const orderA = convertBudgetTypeNameToOrderingKey(a[0]);
    const orderB = convertBudgetTypeNameToOrderingKey(b[0]);
    return orderA - orderB;
  });

  return {
    name: BudgetTypeLabelModifier[type],
    type,
    valueByYear: tableYears.map((year) => {
      const items = data?.filter((item) => item.fiscalYear === year) ?? [];
      const hasChildrenNegativeValue = items.some(
        (d) => (d.negativeFinalizedAmountCount ?? 0) > 0,
      );
      const allNull = items.every(
        (d) => d.finalizedAmount === null || d.finalizedAmount === undefined,
      );
      const targetYear = Number(year);
      return {
        year: targetYear,
        value: allNull
          ? undefined
          : items.reduce(
              (acc, cur) => BigCalculator.add(acc, cur.finalizedAmount ?? 0),
              0,
            ),
        hasNegativeValue: hasChildrenNegativeValue,
      };
    }),
    children: groupedByBudgetTypeName.length
      ? groupedByBudgetTypeName.map(([budgetTypeName, data]) => {
          return {
            type,
            name: budgetTypeName,
            valueByYear: data.map((d) => ({
              year: d.fiscalYear,
              value: d.finalizedAmount,
              hasNegativeValue: (d.negativeFinalizedAmountCount ?? 0) > 0,
            })),
          };
        })
      : undefined,
  };
};

/** 前年度からの繰越行の取得 */
const getCarriedForwardBudgetRow = (
  data: z.infer<typeof ConsecutiveBudgetsModel> | undefined,
  tableYears: number[],
): BudgetAndExecutionTableType => {
  return {
    type: 'carried-forward',
    name: t('budget-brought-forward-from-prior'),
    valueByYear: tableYears.map((year) => {
      const item = data?.find((item) => item.fiscalYear === year);
      return {
        year,
        value: item?.finalizedAmount,
        hasNegativeValue: (item?.negativeFinalizedAmountCount ?? 0) > 0,
      };
    }),
  };
};

/** 合計行の取得 */
const getTotalBudgetAmountRow = (
  tableYears: number[],
  consecutiveBudgets?: z.infer<typeof ConsecutiveBudgetsModel>,
): BudgetAndExecutionTableType => {
  return {
    type: 'total',
    name: t('total-budget-amount'),
    valueByYear: tableYears.map((year) => {
      const consecutiveBudgetsByYear = consecutiveBudgets?.filter(
        (d) => d.fiscalYear === year,
      );
      const hasNegativeValue = consecutiveBudgetsByYear?.some(
        (d) =>
          (d.negativeFinalizedAmountCount ?? 0) > 0 ||
          (d.negativeRequestedAmountCount ?? 0) > 0,
      );
      return {
        year,
        value: calculateTotalBudgetAmount(year, consecutiveBudgetsByYear),
        hasNegativeValue: !!hasNegativeValue,
      };
    }),
  };
};

/** 執行額行の取得 */
const getExecutionAmountRow = (
  tableColumnKeys: number[],
  consecutiveExecutions?: z.infer<typeof ConsecutiveExecutionsModel>,
): BudgetAndExecutionTableType => {
  return {
    type: 'execution',
    name: t('execution-amount'),
    valueByYear: tableColumnKeys.map((year) => {
      const execution = consecutiveExecutions?.find(
        (d) => d.fiscalYear === Number(year),
      );
      return {
        year,
        value: execution?.amount,
        hasNegativeValue: (execution?.negativeAmountCount ?? 0) > 0,
      };
    }),
  };
};

/** 執行率行の取得 */
const getExecutionRate = (
  tableYears: number[],
  consecutiveBudgets?: z.infer<typeof ConsecutiveBudgetsModel>,
  consecutiveExecutions?: z.infer<typeof ConsecutiveExecutionsModel>,
): BudgetAndExecutionTableType => {
  return {
    type: 'execution-rate',
    name: t('execution-rate'),
    valueByYear: tableYears.map((year) => {
      const totalBudgetAmount = calculateTotalBudgetAmount(
        year,
        consecutiveBudgets,
      );
      const execution = consecutiveExecutions?.find(
        (d) => d.fiscalYear === Number(year),
      );
      if (!totalBudgetAmount || typeof execution?.amount !== 'number') {
        return {
          year,
          value: undefined,
          hasNegativeValue: false,
        };
      }
      const rate = BigCalculator.divide(execution.amount, totalBudgetAmount);
      return {
        year,
        value: formatPercentage(rate, { maximumFractionDigits: 1 }),
        hasNegativeValue: false,
      };
    }),
  };
};

/** その他行の取得 */
const getOtherRows = ({
  tableYears,
  carriedForwardData,
  consecutiveBudgets,
  consecutiveExecutions,
}: {
  tableYears: number[];
  carriedForwardData: z.infer<typeof ConsecutiveBudgetsModel> | undefined;
  consecutiveBudgets: z.infer<typeof ConsecutiveBudgetsModel> | undefined;
  consecutiveExecutions: z.infer<typeof ConsecutiveExecutionsModel> | undefined;
}): BudgetAndExecutionTableType => {
  return {
    hideInPdf: true,
    type: 'others',
    name: t('others'),
    valueByYear: null,
    children: [
      getBudgetAndExecutionRatio(
        tableYears,
        consecutiveBudgets,
        consecutiveExecutions,
      ),
      {
        type: 'others',
        name: t('budget-carried-forward-to-next'),
        valueByYear:
          tableYears?.flatMap<ValueByYear>((year) => {
            const targets = carriedForwardData?.filter(
              // 翌年度事業の前年度からの繰越額が設定されるため
              (d) => d.fiscalYear === year + 1,
            );
            const hasNegativeValue = !!targets?.some(
              (d) => (d.negativeFinalizedAmountCount ?? 0) > 0,
            );

            return [
              {
                year,
                value: targets?.length
                  ? targets.reduce(
                      (acc, cur) =>
                        BigCalculator.add(acc, cur.finalizedAmount ?? 0),
                      0,
                    )
                  : undefined,
                hasNegativeValue,
              },
            ];
          }) ?? [],
      },
    ],
  };
};

const getBudgetAndExecutionRatio = (
  tableYears: number[],
  consecutiveBudgets?: z.infer<typeof ConsecutiveBudgetsModel>,
  consecutiveExecutions?: z.infer<typeof ConsecutiveExecutionsModel>,
): BudgetAndExecutionTableType => {
  const valueByYear = tableYears.map((year) => {
    const totalBudgetAmount =
      consecutiveBudgets?.reduce((acc, cur) => {
        if (
          cur.fiscalYear !== year ||
          !(
            cur.budgetType === 'initial' || cur.budgetType === 'supplementary'
          ) ||
          typeof cur.finalizedAmount !== 'number'
        ) {
          return acc;
        }
        return BigCalculator.add(acc, cur.finalizedAmount);
      }, 0) ?? 0;

    const execution = consecutiveExecutions?.find(
      (d) => d.fiscalYear === Number(year),
    );

    if (!totalBudgetAmount || typeof execution?.amount !== 'number') {
      return {
        year,
        value: undefined,
        hasNegativeValue: false,
      };
    }

    const ratio = BigCalculator.divide(execution.amount, totalBudgetAmount);
    return {
      year,
      value: formatPercentage(ratio, { maximumFractionDigits: 1 }),
      hasNegativeValue: false,
    };
  });

  return {
    type: 'others',
    name: t('budget-and-execution-ratio'),
    valueByYear,
  };
};
